/**
 * Inline matchups filter module.
 * Replaces the legacy modal with a simple single-select list that
 * keeps the filters store as the source of truth.
 */

/**
 * @typedef {import('../types').FiltersStore} FiltersStore
 */

const MATCHUPS_HIDE_STYLE_ID = 'props-matchups-hide-style';
let tableObserver = null;
let tableObserverTarget = null;

function ensureMatchupsHideStyle() {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.getElementById(MATCHUPS_HIDE_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = MATCHUPS_HIDE_STYLE_ID;
  style.textContent =
    '.props-comparison-table tbody tr.is-hidden-by-matchups{display:none !important;}' +
    '.props-comparison-table tbody tr[data-matchups-visible="0"]{display:none !important;}';
  document.head.appendChild(style);
}

function getFiltersScope(root) {
  /** @type {HTMLElement[]} */
  const candidates = [];
  if (root instanceof HTMLElement) {
    const scoped = root.closest('.props-odds.props-odds-scope');
    if (scoped) {
      candidates.push(scoped);
    }
  }
  document.querySelectorAll('.props-odds.props-odds-scope').forEach((scope) => {
    if (scope instanceof HTMLElement && !candidates.includes(scope)) {
      candidates.push(scope);
    }
  });
  const withTable = candidates.find(
    (scope) =>
      scope instanceof HTMLElement && scope.querySelector('table.props-comparison-table')
  );
  return withTable || candidates[0] || null;
}

function getTableRoot(filtersRoot) {
  const scope = getFiltersScope(filtersRoot);
  if (scope instanceof HTMLElement && scope.querySelector('table.props-comparison-table')) {
    return scope;
  }
  const table = document.querySelector('table.props-comparison-table');
  if (table instanceof HTMLElement) {
    return table.closest('.props-odds.props-odds-scope') || table.parentElement;
  }
  return scope instanceof HTMLElement ? scope : null;
}

function getMatchupRows(filtersRoot) {
  const root = getTableRoot(filtersRoot);
  if (root instanceof HTMLElement) {
    return Array.from(
      root.querySelectorAll('table.props-comparison-table tbody tr')
    );
  }
  return Array.from(document.querySelectorAll('table.props-comparison-table tbody tr'));
}

function ensureTableObserver(filtersRoot, callback) {
  if (typeof MutationObserver !== 'function') {
    return;
  }
  const target = getTableRoot(filtersRoot);
  if (!(target instanceof HTMLElement)) {
    return;
  }
  if (tableObserver && tableObserverTarget === target) {
    return;
  }
  if (tableObserver) {
    tableObserver.disconnect();
  }
  tableObserverTarget = target;
  tableObserver = new MutationObserver((mutations) => {
    if (!Array.isArray(mutations)) {
      return;
    }
    const changed = mutations.some((mutation) => {
      if (mutation.type === 'childList') {
        return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
      }
      return false;
    });
    if (changed) {
      callback?.();
    }
  });
  tableObserver.observe(target, { childList: true, subtree: true });
}

function refreshShowToggle() {
  try {
    if (typeof window === 'undefined' || typeof window.propsShowToggleReady !== 'function') {
      return;
    }
    window.propsShowToggleReady((api) => {
      if (!api || typeof api.apply !== 'function') {
        return;
      }
      const scope = getTableRoot(filtersRootCache || null) || document.querySelector('.props-odds.props-odds-scope');
      if (scope instanceof HTMLElement) {
        api.apply(scope);
      }
    });
  } catch {
    // Ignore toggle refresh errors; matchups filtering should keep working.
  }
}

/** @type {HTMLElement | null} */
let filtersRootCache = null;

function applyMatchupFilters(filtersRoot, getActiveId) {
  ensureMatchupsHideStyle();
  const activeId = (getActiveId?.() || '').trim();
  const rows = getMatchupRows(filtersRoot);
  const shouldFilter = activeId !== '';
  rows.forEach((row) => {
    if (!(row instanceof HTMLTableRowElement)) {
      return;
    }
    const gameId = row.getAttribute('data-game-id') || '';
    const visible = !shouldFilter || (gameId && gameId === activeId);
    row.hidden = !visible;
    row.dataset.matchupsVisible = visible ? '1' : '0';
    row.classList.toggle('is-hidden-by-matchups', !visible);
  });
  const tableRoot = getTableRoot(filtersRoot);
  const table =
    tableRoot instanceof HTMLElement
      ? tableRoot.querySelector('table.props-comparison-table')
      : document.querySelector('table.props-comparison-table');
  if (table) {
    table.classList.toggle('is-filtered-by-matchups', shouldFilter);
  }
  // When filtering to a matchup, expand all rows so the filtered set is fully visible.
  if (shouldFilter && typeof window !== 'undefined' && typeof window.propsShowToggleReady === 'function') {
    const scope = tableRoot instanceof HTMLElement ? tableRoot : document.querySelector('.props-odds.props-odds-scope');
    try {
      window.propsShowToggleReady((api) => {
        if (api && typeof api.forceExpand === 'function') {
          api.forceExpand(scope || document.body, 'matchup-filter');
        }
      });
    } catch (e) {
      // ignore show toggle errors
    }
  }
  // Disable automatic re-run on DOM mutations to prevent recursive refresh/hangs.
  // ensureTableObserver(filtersRoot, () => applyMatchupFilters(filtersRoot, getActiveId));
  filtersRootCache = filtersRoot instanceof HTMLElement ? filtersRoot : filtersRootCache;
  refreshShowToggle();
}

function syncUrlParam(matchupId) {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (matchupId) {
      url.searchParams.set('matchups', matchupId);
      url.searchParams.set('matchup_ids', matchupId);
    } else {
      url.searchParams.delete('matchups');
      url.searchParams.delete('matchup_ids');
    }
    window.history.replaceState?.(null, '', url.toString());
  } catch {
    try {
      const params = new URLSearchParams(window.location.search);
      if (matchupId) {
        params.set('matchups', matchupId);
        params.set('matchup_ids', matchupId);
      } else {
        params.delete('matchups');
        params.delete('matchup_ids');
      }
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState?.(null, '', next);
    } catch {
      // Silent fallback; we tried.
    }
  }
}

function emitInlineNavigate(scope, inlineEnabled, matchupId) {
  // Skip inline navigation entirely; rely on local filtering to avoid UI hangs.
}

function updateSummary(summaryEl, cardMap, activeId) {
  if (!(summaryEl instanceof HTMLElement)) {
    return;
  }
  const activeCard = cardMap.get(activeId) || cardMap.get('') || null;
  if (activeCard) {
    // Always prefer matchup-card-label to avoid rendering HTML from matchup-card-teams
    const label = activeCard.querySelector('.matchup-card-label');
    if (label) {
      summaryEl.textContent = label.textContent?.trim() || summaryEl.textContent || '';
    } else {
      // Fallback: extract team codes only, not the full HTML
      const teams = activeCard.querySelector('.matchup-card-teams');
      if (teams) {
        const teamCodes = Array.from(teams.querySelectorAll('.team-code'))
          .map((el) => el.textContent?.trim())
          .filter(Boolean)
          .join(' vs ');
        summaryEl.textContent = teamCodes || summaryEl.textContent || '';
      }
    }
  }
}

function updateCardState(cardMap, activeId) {
  cardMap.forEach((card, id) => {
    if (!(card instanceof HTMLElement)) {
      return;
    }
    const isActive = (activeId === '' && id === '') || id === activeId;
    card.classList.toggle('is-active', isActive);
    card.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
}

export function registerMatchupsModule(store) {
  if (!store || typeof document === 'undefined') {
    return;
  }

  const filtersRoot = document.querySelector('[data-props-matchups-inline="true"]');
  if (!(filtersRoot instanceof HTMLElement)) {
    return;
  }

  filtersRootCache = filtersRoot;
  ensureMatchupsHideStyle();

  const listRoot = filtersRoot.querySelector('[data-props-matchups-list="true"]');
  if (!(listRoot instanceof HTMLElement)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[filters] matchups inline list missing root element');
    }
    return;
  }

  const summaryEl = filtersRoot.querySelector('[data-props-matchups-summary]');
  const toggleButton = filtersRoot.querySelector('[data-props-matchups-toggle]');
  const bodyEl = filtersRoot.querySelector('[data-props-matchups-body]');
  const cardMap = new Map();
  listRoot.querySelectorAll('[data-matchup-id]').forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    if (node.classList.contains('is-empty')) {
      return;
    }
    const id = node.getAttribute('data-matchup-id') || '';
    cardMap.set(id, node);
  });

  if (!cardMap.size) {
    return;
  }

  const inlineScope = getFiltersScope(filtersRoot);
  const inlineEnabled =
    inlineScope?.getAttribute('data-inline-markets') === 'true' ||
    !!store.config?.featureFlags?.inlineMarketsEnabled;

  let currentMatchupId = '';
  let readyForNavigate = false;
  let expanded = false;
  const resizeListeners = new Set();

  const getActiveId = () => currentMatchupId;

  function handleOutside(event) {
    if (!(filtersRoot instanceof HTMLElement)) {
      return;
    }
    if (event.target instanceof Node && filtersRoot.contains(event.target)) {
      return;
    }
    setExpanded(false);
  }

  function setExpanded(next) {
    const shouldExpand = Boolean(next);
    if (expanded === shouldExpand) {
      return;
    }
    expanded = shouldExpand;
    if (bodyEl instanceof HTMLElement) {
      bodyEl.hidden = !expanded;
    }
    filtersRoot.classList.toggle('is-open', expanded);
    if (toggleButton instanceof HTMLElement) {
      toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (expanded) {
      positionPanel();
      const handleResize = () => positionPanel();
      window.addEventListener('resize', handleResize);
      resizeListeners.add(handleResize);
    } else {
      resizeListeners.forEach((listener) => {
        window.removeEventListener('resize', listener);
      });
      resizeListeners.clear();
      if (bodyEl instanceof HTMLElement) {
        bodyEl.style.top = '';
        bodyEl.style.maxHeight = '';
        bodyEl.style.position = '';
        bodyEl.style.left = '';
        bodyEl.style.right = '';
      }
    }
    if (expanded) {
      document.dispatchEvent(new CustomEvent('props-dropdown-open', { detail: 'matchups' }));
      document.addEventListener('pointerdown', handleOutside);
    } else {
      document.removeEventListener('pointerdown', handleOutside);
    }
  }

  setExpanded(false);

  toggleButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded(!expanded);
  });

  // Close button handler
  const closeButton = filtersRoot.querySelector('[data-action="close-matchups"]');
  closeButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded(false);
  });

  document.addEventListener('props-dropdown-open', (event) => {
    if (event?.detail === 'matchups') {
      return;
    }
    setExpanded(false);
  });

  function positionPanel() {
    if (!(bodyEl instanceof HTMLElement) || !(toggleButton instanceof HTMLElement)) {
      return;
    }
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
    if (!isMobile) {
      bodyEl.style.position = 'absolute';
      bodyEl.style.top = 'calc(100% + 8px)';
      bodyEl.style.left = '0';
      bodyEl.style.right = 'auto';
      bodyEl.style.maxHeight = '480px';
      return;
    }
    const rect = toggleButton.getBoundingClientRect();
    const padding = 12;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const minTop = viewportTop + 72; // stay below sticky headers/banners on mobile
    const top = Math.max(rect.bottom + 12, minTop);
    const available = Math.max(180, viewportHeight - (top - viewportTop) - padding);
    bodyEl.style.top = `${top}px`;
    bodyEl.style.left = `${padding}px`;
    bodyEl.style.right = `${padding}px`;
    bodyEl.style.maxHeight = `${Math.min(available, 720)}px`;
  }

  function handleStateChange(state, { skipNavigate } = {}) {
    const nextId = typeof state?.activeMatchupId === 'string' ? state.activeMatchupId : '';
    if (nextId === currentMatchupId) {
      return;
    }
    currentMatchupId = nextId;
    updateCardState(cardMap, nextId);
    updateSummary(summaryEl, cardMap, nextId);
    applyMatchupFilters(filtersRoot, getActiveId);
    syncUrlParam(nextId);
    if (!skipNavigate && readyForNavigate) {
      emitInlineNavigate(inlineScope, inlineEnabled, nextId);
    }
  }

  function requestSelection(id) {
    const nextId = typeof id === 'string' ? id.trim() : '';
    if (store.actions && typeof store.actions.setActiveMatchup === 'function') {
      store.actions.setActiveMatchup(nextId);
    } else {
      store.setState?.({ activeMatchupId: nextId });
    }
    setExpanded(false);
  }

  cardMap.forEach((card, id) => {
    // Handle pointerdown so taps/clicks on fast devices register even if the panel closes immediately.
    card.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      requestSelection(id);
      // Immediately update UI/apply filter in case store/refresh is slow
      updateCardState(cardMap, id);
      updateSummary(summaryEl, cardMap, id);
      applyMatchupFilters(filtersRoot, () => id);
    });

    card.addEventListener('click', (event) => {
      event.preventDefault();
      requestSelection(id);
      // Immediately update UI/apply filter in case store/refresh is slow
      updateCardState(cardMap, id);
      updateSummary(summaryEl, cardMap, id);
      applyMatchupFilters(filtersRoot, () => id);
    });
  });

  const unsubscribe = store.subscribe((state) => {
    handleStateChange(state, { skipNavigate: !readyForNavigate });
    if (!readyForNavigate) {
      readyForNavigate = true;
    }
  });

  const handleRefresh = () => {
    updateCardState(cardMap, currentMatchupId);
    updateSummary(summaryEl, cardMap, currentMatchupId);
    applyMatchupFilters(filtersRoot, getActiveId);
  };

  document.addEventListener('propsOdds:refresh:success', handleRefresh, true);

  const teardown = () => {
    unsubscribe?.();
    document.removeEventListener('propsOdds:refresh:success', handleRefresh, true);
    document.removeEventListener('pointerdown', handleOutside);
    if (tableObserver) {
      tableObserver.disconnect();
      tableObserver = null;
      tableObserverTarget = null;
    }
  };

  filtersRoot.addEventListener('filters:teardown', teardown, { once: true });
}

